/**
 * MePet — Phase 4 pet care home
 *   /me/health/pet
 *   • Pet profile hero (breed / microchip / insurance)
 *   • Vaccination / deworming / next visit / insurance status pills
 *   • Feature grid: Vaccination · Deworming · Heat cycle · Grooming · Reminders
 *   • Care tip card
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { custApi, formatErr } from "../../lib/customerApi";
import {
  ArrowLeft, PawPrint, Calendar, ShieldCheck, Syringe, Bug, Heart,
  Plus, Trash, ArrowRight, Users, Scissors, IdentificationBadge,
} from "@phosphor-icons/react";

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function prettyDate(iso) {
  if (!iso) return "—";
  return new Date(iso + "T00:00:00").toLocaleDateString(undefined, {
    day: "numeric", month: "short", year: "numeric",
  });
}
function BackHeader({ to, title, subtitle }) {
  return (
    <div className="flex items-start gap-3">
      <Link to={to} className="w-9 h-9 rounded-full bg-white border border-gray-100 flex items-center justify-center shrink-0 hover:bg-gray-50">
        <ArrowLeft size={16} weight="bold" className="text-gray-700"/>
      </Link>
      <div className="flex-1 min-w-0">
        <h1 className="font-display font-black text-[22px] leading-tight tracking-tight text-[#111827]">
          {title}
        </h1>
        {subtitle && <div className="text-[12px] text-gray-500 mt-0.5">{subtitle}</div>}
      </div>
    </div>
  );
}

export function PetHome() {
  const [pets, setPets] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [summary, setSummary] = useState(null);
  const [tips, setTips] = useState([]);
  const [showEvent, setShowEvent] = useState(null); // 'deworming' | 'heat' | 'grooming' | 'flea_tick'
  const [form, setForm] = useState({ date: todayISO(), next_due_date: "", notes: "" });
  const [seedBusy, setSeedBusy] = useState(false);

  const loadPets = async () => {
    try {
      const r = await custApi.get("/me/loved-ones");
      const list = (r.data.loved_ones || []).filter(l => l.kind === "pet");
      setPets(list);
      if (list.length && !selectedId) setSelectedId(list[0].id);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const load = async () => {
    if (!selectedId) { setSummary(null); return; }
    try {
      const [s, t] = await Promise.all([
        custApi.get(`/me/pets/${selectedId}/summary`),
        custApi.get("/me/care-circle/tips?kind=pet"),
      ]);
      setSummary(s.data);
      setTips(t.data.tips || []);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { loadPets(); }, []);
  useEffect(() => { load(); /* eslint-disable-line */ }, [selectedId]);

  const saveEvent = async () => {
    try {
      await custApi.post("/me/pet-events", {
        loved_one_id: selectedId,
        kind: showEvent,
        date: form.date,
        next_due_date: form.next_due_date || null,
        notes: form.notes || null,
      });
      toast.success("Logged");
      setForm({ date: todayISO(), next_due_date: "", notes: "" });
      setShowEvent(null);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const seedVac = async () => {
    setSeedBusy(true);
    try {
      const r = await custApi.post("/me/vaccine-reminders/seed-pet", { loved_one_id: selectedId });
      toast.success(`${r.data.added} pet vaccines added`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    setSeedBusy(false);
  };

  if (pets.length === 0) {
    return (
      <div className="space-y-4 px-4 py-4">
        <BackHeader to="/me/health" title="Pet care"/>
        <div className="border border-dashed border-[#0F766E] bg-[#CCFBF1] rounded-md p-4 text-center">
          <PawPrint size={24} weight="duotone" className="mx-auto mb-1 text-[#0F766E]"/>
          <div className="text-sm font-semibold text-[#1A1A1A]">Add your pet</div>
          <div className="text-[11px] text-[#5C5A55] mt-1">
            Go to Profile → Health Profile, add a loved one with Care Kind = <b>Pet</b>.
          </div>
          <Link to="/me/profile#health-profile"
                className="mt-3 inline-block h-9 px-4 leading-9 bg-[#0F766E] hover:bg-[#065F46] text-white rounded-md text-xs font-medium">
            Go to Profile
          </Link>
        </div>
      </div>
    );
  }

  const pet = summary?.pet;
  const dw = summary?.deworming;
  const heat = summary?.heat;
  const vac = summary?.vaccines;
  const ins = summary?.insurance;

  return (
    <div className="space-y-4 px-4 py-4 pb-8" data-testid="me-pet">
      <BackHeader to="/me/health" title="Pet care"
                    subtitle="Vaccines · deworming · insurance"/>

      {/* Pet picker */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4">
        {pets.map(lo => {
          const on = lo.id === selectedId;
          return (
            <button key={lo.id} onClick={() => setSelectedId(lo.id)}
                    data-testid={`pet-picker-${lo.id}`}
                    className={`h-10 px-3 shrink-0 rounded-full border text-xs font-medium inline-flex items-center gap-1.5 ${
                      on ? "bg-[#0F766E] text-white border-[#0F766E]"
                         : "bg-white text-[#1A1A1A] border-[#E5E2D9]"
                    }`}>
              <PawPrint size={12} weight="fill"/>
              {lo.name}
            </button>
          );
        })}
      </div>

      {/* Pet card */}
      {pet && (
        <div className="rounded-3xl bg-white border border-[#CCFBF1] p-4 shadow-[0_20px_50px_rgba(15,118,110,0.10)]"
             data-testid="pet-card">
          <div className="flex items-start gap-3">
            <div className="w-16 h-16 rounded-2xl bg-[#0F766E] text-white font-black text-2xl inline-flex items-center justify-center shrink-0">
              <PawPrint size={26} weight="fill"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-black text-xl text-[#065F46]">
                {pet.name} <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#DCFCE7] text-[#15803D] font-bold uppercase tracking-widest ml-1">{pet.age}y {pet.gender||""}</span>
              </div>
              <div className="text-[11px] text-gray-600 mt-0.5">
                {pet.breed || "Mixed breed"}
                {pet.date_of_birth ? ` · DOB ${prettyDate(pet.date_of_birth)}` : ""}
              </div>
              {pet.microchip && (
                <div className="text-[11px] text-gray-600 mt-0.5 inline-flex items-center gap-1">
                  <IdentificationBadge size={11}/> Microchip <span className="font-mono">{pet.microchip}</span>
                </div>
              )}
            </div>
          </div>

          {/* Status pills */}
          <div className="grid grid-cols-4 gap-2 mt-4">
            <StatusPill Icon={Syringe} label="Vaccines"
                        value={vac?.status === "up-to-date" ? "Up to date"
                                : vac?.status === "due-soon" ? `Due in ${vac.next_in_days ?? "-"}d`
                                : "Not set"}
                        color={vac?.status === "up-to-date" ? "#15803D"
                                : vac?.status === "due-soon" ? "#EA580C" : "#8A8780"}
                        bg={vac?.status === "up-to-date" ? "#DCFCE7"
                                : vac?.status === "due-soon" ? "#FFEDD5" : "#F3F4F6"}/>
            <StatusPill Icon={Bug} label="Deworming"
                        value={dw?.days_until_next != null
                                ? (dw.days_until_next < 0 ? "Overdue" : `Due ${dw.days_until_next}d`)
                                : "Not set"}
                        color={dw?.days_until_next != null && dw.days_until_next < 14
                                ? "#DC2626" : "#EA580C"}
                        bg={dw?.days_until_next != null && dw.days_until_next < 14
                              ? "#FEE2E2" : "#FFEDD5"}/>
            <StatusPill Icon={Heart} label="Heat cycle"
                        value={heat?.days_until_next != null
                                ? `Due ${heat.days_until_next}d`
                                : "Not tracked"}
                        color="#EC4899" bg="#FCE7F3"/>
            <StatusPill Icon={ShieldCheck} label="Insurance"
                        value={ins?.active ? "Active" : "Inactive"}
                        color={ins?.active ? "#2563EB" : "#8A8780"}
                        bg={ins?.active ? "#DBEAFE" : "#F3F4F6"}/>
          </div>
        </div>
      )}

      {/* Feature grid */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-gray-400 font-bold mb-2">
          Care actions
        </div>
        <div className="grid grid-cols-4 gap-2" data-testid="pet-features">
          <Tile Icon={Syringe} label="Vaccination" color="#15803D" bg="#DCFCE7"
                to="/me/health/vaccines"/>
          <Tile Icon={Bug} label="Deworming" color="#EA580C" bg="#FFEDD5"
                onClick={() => setShowEvent("deworming")}/>
          <Tile Icon={Heart} label="Heat" color="#EC4899" bg="#FCE7F3"
                onClick={() => setShowEvent("heat")}/>
          <Tile Icon={Scissors} label="Grooming" color="#7C3AED" bg="#EDE9FE"
                onClick={() => setShowEvent("grooming")}/>
          <Tile Icon={Calendar} label="Bookings" color="#2563EB" bg="#DBEAFE"
                to="/me/health/bookings"/>
          <Tile Icon={ShieldCheck} label="Insurance" color="#0891B2" bg="#CFFAFE"
                to="/me/profile#health-profile"/>
          <Tile Icon={PawPrint} label="Meals" color="#B45309" bg="#FEF3C7"
                to="/me/services"/>
          <Tile Icon={Users} label="Vet SOS" color="#DC2626" bg="#FEE2E2"
                to="/me/health/emergency"/>
        </div>
      </div>

      {/* Event log form */}
      {showEvent && (
        <div className="border-2 border-dashed border-[#0F766E] rounded-md p-3 bg-[#F0FDFA] space-y-2"
             data-testid="pet-event-form">
          <div className="text-[10px] uppercase tracking-widest text-[#065F46] font-bold">
            Log {showEvent.replace("_", " ")}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Date</label>
              <input type="date" value={form.date}
                     onChange={e => setForm(s => ({ ...s, date: e.target.value }))}
                     className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Next due</label>
              <input type="date" value={form.next_due_date}
                     onChange={e => setForm(s => ({ ...s, next_due_date: e.target.value }))}
                     className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
            </div>
          </div>
          <input value={form.notes}
                 onChange={e => setForm(s => ({ ...s, notes: e.target.value }))}
                 placeholder="Notes"
                 className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={saveEvent}
                    data-testid="pet-event-save"
                    className="h-9 bg-[#0F766E] hover:bg-[#065F46] text-white rounded-md text-sm font-medium">Save</button>
            <button onClick={() => setShowEvent(null)}
                    className="h-9 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
          </div>
        </div>
      )}

      {/* Seed vaccine schedule */}
      {vac && vac.total === 0 && (
        <button onClick={seedVac} disabled={seedBusy}
                data-testid="pet-seed"
                className="w-full h-11 border-2 border-[#0F766E] text-[#0F766E] hover:bg-[#DCFCE7] rounded-xl text-sm font-medium inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
          <Syringe size={13} weight="fill"/> {seedBusy ? "Adding…" : "Seed pet vaccine schedule (6 shots)"}
        </button>
      )}

      {/* Care tip */}
      {tips[0] && (
        <div className="rounded-2xl p-4 bg-gradient-to-br from-[#FEF3C7] to-[#FDE68A] border border-yellow-200">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#B45309]">
            {tips[0].category} · {tips[0].read_min} min read
          </div>
          <div className="font-display font-bold text-[14px] text-[#78350F] mt-1 leading-tight">
            {tips[0].title}
          </div>
          <div className="text-[12px] text-[#78350F] mt-1 leading-snug">
            {tips[0].body}
          </div>
        </div>
      )}
    </div>
  );
}

function StatusPill({ Icon, label, value, color, bg }) {
  return (
    <div className="rounded-xl p-2 text-center" style={{ backgroundColor: bg }}>
      <Icon size={14} weight="fill" style={{ color }}/>
      <div className="text-[10px] font-semibold mt-0.5 leading-tight" style={{ color }}>{label}</div>
      <div className="text-[10px] font-black leading-tight mt-0.5" style={{ color }}>{value}</div>
    </div>
  );
}

function Tile({ Icon, label, color, bg, to, onClick }) {
  const inner = (
    <div className="bg-white rounded-2xl p-3 border border-gray-100 shadow-[0_4px_12px_rgba(0,0,0,0.03)] hover:shadow-md active:scale-[0.97] transition-all text-center">
      <div className="w-10 h-10 rounded-2xl inline-flex items-center justify-center mx-auto"
           style={{ backgroundColor: bg, color }}>
        <Icon size={18} weight="duotone"/>
      </div>
      <div className="text-[11px] font-semibold text-[#1A1A1A] mt-1.5 leading-tight">{label}</div>
    </div>
  );
  if (onClick) return <button onClick={onClick} data-testid={`pet-tile-${label.toLowerCase()}`}>{inner}</button>;
  return <Link to={to} data-testid={`pet-tile-${label.toLowerCase()}`}>{inner}</Link>;
}
